'use client'

import Image from 'next/image'
import GradientText from './GradientText'
import { useInView } from '../hooks/useInView'
import { trackEvent } from '../utils/analytics'

export default function Pricing() {
  const features = [
    "Generate business canvas for 3 different ventures",
    "Run 15 marketing campaign simulations",
    "Market insights",
    "Customer persona analysis",
    "Competitor analysis"
  ]

  const { ref: frameRef, inView } = useInView({ 
    threshold: 0.05, 
    rootMargin: '100px' 
  })

  const { ref: headerRef, inView: headerInView } = useInView({ 
    threshold: 0.1, 
    rootMargin: '0px' 
  })

  return (
    <section id="pricing" className="relative bg-gray-50 pt-4 pb-8 sm:pb-12">
      {/* Corner-radiused frame */}
      <div 
        ref={frameRef}
        className={`w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] min-h-[80vh] sm:min-h-[calc(100vh+3rem)] md:min-h-[calc(100vh+5rem)] mx-auto rounded-2xl sm:rounded-3xl transition-all duration-1000 ease-out ${
          inView 
            ? 'translate-y-0 scale-100' 
            : 'translate-y-8 scale-95'
        }`} 
        style={{ backgroundColor: '#000914', transitionDelay: inView ? '120ms' : '0ms' }}
      >
        <div className="relative z-10">
          <div className="container-stripe py-12 sm:py-20 lg:py-24">
            {/* Section Header */}
            <div ref={headerRef} className="text-center mb-12 sm:mb-20 px-4 sm:px-0">
              <div className={`glassmorphism-button group inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 text-white text-xs font-medium mb-6 sm:mb-8 cursor-default transition-all duration-700 ease-out ${
                headerInView 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-4'
              }`}>
                THE PRICING
              </div>
              <h2 className={`text-3xl sm:text-4xl md:text-6xl font-light mb-4 text-balance text-center max-w-5xl mx-auto text-white leading-relaxed drop-shadow-lg tracking-tight transition-all duration-700 ease-out ${
                headerInView 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: headerInView ? '100ms' : '0ms' }}>
                Start Building Your{' '}
                <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
                  Venture Strategy
                </GradientText>
              </h2>
            </div>

            {/* Pricing Card */}
            <div className="max-w-lg mx-auto mb-12 sm:mb-16 px-4 sm:px-0">
              <div
                className={`relative overflow-hidden rounded-stripe-lg border border-gray-900 transition-all duration-700 ease-out ${
                  inView 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ 
                  transitionDelay: inView ? '300ms' : '0ms',
                  boxShadow: 'inset 0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Background Image with Lazy Loading */}
                <Image
                  src="/images/ship2.webp"
                  alt=""
                  fill
                  loading="lazy"
                  className="object-cover object-center z-[-10]"
                  sizes="(max-width: 768px) 100vw, 600px"
                />
                
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black/80 rounded-stripe-lg transition-all duration-300"></div>
                
                {/* Content */}
                <div className="relative z-10 p-6 sm:p-8 lg:p-10">
                  {/* Free Badge */}
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3 sm:mb-4 text-white leading-tight tracking-tight">
                      Free Forever
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg font-light text-blue-100/95 leading-relaxed drop-shadow-sm">
                      Everything you need to validate and launch your venture
                    </p>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-4 sm:space-y-5 mb-8 sm:mb-10">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg
                          className="w-5 h-5 sm:w-6 sm:h-6 mr-3 sm:mr-4 mt-0.5 flex-shrink-0 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm sm:text-base md:text-lg font-light text-blue-100/95 leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <a
                    href="https://app.venturecompass.ai/signup"
                    className="glassmorphism-button group w-full inline-flex items-center justify-center px-6 py-4 sm:px-8 sm:py-5 text-white font-medium text-base sm:text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2"
                    onClick={() => trackEvent('cta_click', { location: 'pricing', text: 'Start Using for Free' })}
                  >
                    Start Using for Free
                    <svg className="ml-2 w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
